import {ButtonLink} from '@/components/ui/button-link/ButtonLink'
import {Button} from '@/components/ui/button/Button'
import {Divider} from '@/components/ui/divider/Divider'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {PageCardWrapper, PageContent, PageHeading} from '@/components/ui/page-atoms/PageAtoms'
import {useTranslation} from '@/translations/i18n.tsx'
import {Helmet} from 'react-helmet'
import {ScrollArea} from '@/components/ui/scroll-area/ScrollArea'
import {Scrollbar} from '@/components/ui/scrollbar/Scrollbar'
import {UnsavedBlocker} from '@/components/commons/unsaved-blocker/UnsavedBlocker'
import {FormProvider, useForm, useWatch} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {DiseaseExtended, TreatmentRequestValidationSchema} from '@/features/treatment-requests/types'
import {FormGridSection, FormGridSectionItem, FormGridSectionTitle} from '@/components/ui/form-atoms/FormAtoms'
import {TreatmentRequestPatientForm} from '@/features/treatment-requests/components/treatment-request-patient-form/TreatmentRequestPatientForm'
import {TreatmentRequestTreatmentForm} from '@/features/treatment-requests/components/treatment-request-treatment-form/TreatmentRequestTreatmentForm'
import {getPatientServiceType} from '@/utilities/helpers'
import {useGetPatient} from '@/features/patients/services/useGetPatient'
import {TreatmentRequestScoresForm} from '@/features/treatment-requests/components/treatment-request-scores-form/TreatmentRequestScoresForm'
import {TreatmentRequestNotesForm} from '@/features/treatment-requests/components/treatment-request-notes-form/TreatmentRequestNotesForm'
import {TreatmentRequestDiagnosisForm} from '@/features/treatment-requests/components/treatment-request-diagnosis-form/TreatmentRequestDiagnosisForm'
import {useEffect, useRef, useState} from 'react'
import {To} from 'react-router-dom'
import {CreateTreatmentRequestConfirmationModal} from '@/features/treatment-requests/components/create-treatment-request-confirmation-modal/CreateTreatmentRequestConfirmationModal'
import {BannerAlert} from '@/components/commons/banner-alert/BannerAlert'
import {useParsedSearchParams} from '@/hooks/useParsedSearchParams'
import {z} from 'zod'
import {useDialogStore} from '@ariakit/react'
import {FormErrorResponse} from '@/types'
import {AxiosError} from 'axios'
import {useMutationState} from '@tanstack/react-query'
import {MUTATION_KEYS} from '@/queryClient'

export const CreateTreatmentRequest = () => {
    const {t} = useTranslation()
    const [isErrorBannerVisible, setIsBannerVisible] = useState(false)
    const [isConfirmationModalVisible, setIsConfirmationModalVisible] = useState(false)
    const viewportRef = useRef<HTMLDivElement>(null)
    const errorBannerRef = useRef<HTMLDivElement>(null)

    const {searchParams, setSearchParams} = useParsedSearchParams(
        z.object({
            patientId: z.string().optional()
        })
    )

    const confirmationDialogStore = useDialogStore({
        open: isConfirmationModalVisible,
        setOpen: setIsConfirmationModalVisible
    })

    // methods
    const form = useForm<TreatmentRequestValidationSchema>({
        mode: 'onBlur',
        resolver: zodResolver(TreatmentRequestValidationSchema),
        defaultValues: {
            diseases: [],
            scores: []
        }
    })

    const onErrorConfirmationModal = (errors: AxiosError<{errors: FormErrorResponse[]}>) => {
        confirmationDialogStore.setOpen(false)
        if (errors?.response?.data.errors) {
            setIsBannerVisible(true)

            if (viewportRef.current) {
                viewportRef.current.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                })
            }
        }
    }

    // form observer
    const patient = useWatch({control: form.control, name: 'patient'})
    const diseases = useWatch({control: form.control, name: 'diseases'})

    // query
    const patientQuery = useGetPatient({
        patientId: (searchParams?.patientId || patient?.id) ?? undefined,
        responseType: 'extended',
        options: {
            enabled: !!(searchParams?.patientId || patient?.id)
        }
    })

    // actions
    const handleSubmit = form.handleSubmit(
        () => confirmationDialogStore.setOpen(true),
        errors => {
            setIsBannerVisible(true)
            if (viewportRef.current) {
                viewportRef.current.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                })
            }
            console.error(errors)
        }
    )

    // side effects
    const onChangePatientSideEffect = () => {
        form.resetField('authorization')
        form.resetField('authorizationDate')
        form.resetField('authorizationToRequest')
        form.resetField('diseaseOnsetDate')
        form.resetField('diseases')
        form.resetField('notes')
        form.resetField('scores')
        form.resetField('sector')
        form.resetField('type')
    }

    useEffect(() => {
        if (searchParams.patientId && patientQuery.data) {
            form.setValue('patient', {
                id: patientQuery.data.id,
                fullName: patientQuery.data.fullName
            })
            setSearchParams({patientId: undefined})
        }
    }, [searchParams?.patientId, patientQuery.data])

    // Sync scores when diseases change - remove subtype if "I° Gruppo" is not present
    useEffect(() => {
        if (diseases) {
            const canDisplaySubType = diseases.some((disease: DiseaseExtended) => disease.name === 'I° Gruppo')
            const currentScores = form.getValues('scores')

            // Filter out subtype scores if diseases don't include "I° Gruppo"
            const filteredScores = currentScores.filter(score => {
                if (score.type === 'subtype' && !canDisplaySubType) {
                    return false
                }
                return true
            })

            // Update scores if they changed (removed subtype)
            if (filteredScores.length !== currentScores.length) {
                form.setValue('scores', filteredScores, {
                    shouldDirty: false,
                    shouldTouch: true,
                    shouldValidate: true
                })
                // Force re-render of components that depend on scores
                form.trigger('scores')
            }
        }
    }, [diseases])

    // Track all create patient mutation states and extract the status of the most recent mutation
    // This is used to determine if there are unsaved changes that should block navigation
    const createTreatmentRequestMutationStatuses = useMutationState({
        filters: {
            mutationKey: [MUTATION_KEYS.CREATE_TREATMENT_REQUEST]
        },
        select: mutation => mutation.state.status
    })

    const createTreatmentRequestMutationStatus =
        createTreatmentRequestMutationStatuses[createTreatmentRequestMutationStatuses.length - 1]

    return (
        <PageCardWrapper>
            <Helmet title={t('treatment_requests:create_meta_title')} />
            <PageHeading align="center" justify="space-between">
                <h2>{t('treatment_requests:rehabilitation_treatment_request')}</h2>
                <Flexbox gap={2}>
                    <ButtonLink to={-1 as To} variant="secondary">
                        {t('commons:cancel')}
                    </ButtonLink>
                    <Button
                        onClick={handleSubmit}
                        variant="primaryNeutral"
                        disabled={
                            !patientQuery.data?.treatmentRequestEligibility.canRequest ||
                            !form.formState.isValid ||
                            form.formState.errors.authorization?.type == 'custom' ||
                            form.formState.errors.type?.type == 'custom'
                        }
                    >
                        {t('commons:add')}
                    </Button>
                </Flexbox>
            </PageHeading>

            <Divider direction="horizontal" />

            <PageContent padding={0}>
                <ScrollArea style={{height: '100%'}} scrollbar={<Scrollbar />} viewportProps={{ref: viewportRef}}>
                    {isErrorBannerVisible && (
                        <>
                            <BannerAlert ref={errorBannerRef} onClose={() => setIsBannerVisible(false)} />
                            <Divider direction="horizontal" />
                        </>
                    )}
                    <UnsavedBlocker
                        isDirty={
                            (createTreatmentRequestMutationStatus == 'idle' || !createTreatmentRequestMutationStatus) &&
                            !!Object.keys(form.formState.dirtyFields).length
                        }
                    >
                        <FormProvider {...form}>
                            <FormGridSection>
                                <FormGridSectionItem>
                                    <FormGridSectionTitle>{t('commons:patient')}</FormGridSectionTitle>
                                </FormGridSectionItem>
                                <FormGridSectionItem>
                                    <TreatmentRequestPatientForm onChangePatient={onChangePatientSideEffect} />
                                </FormGridSectionItem>
                            </FormGridSection>

                            {patientQuery?.data?.id && patientQuery.data.treatmentRequestEligibility.canRequest && (
                                <>
                                    <Divider direction="horizontal" />

                                    <FormGridSection>
                                        <FormGridSectionItem>
                                            <FormGridSectionTitle>{t('commons:diagnosis')}</FormGridSectionTitle>
                                        </FormGridSectionItem>
                                        <FormGridSectionItem>
                                            <TreatmentRequestDiagnosisForm
                                                serviceType={patientQuery.data.serviceType}
                                            />
                                        </FormGridSectionItem>
                                    </FormGridSection>

                                    <Divider direction="horizontal" />

                                    <FormGridSection>
                                        <FormGridSectionItem>
                                            <FormGridSectionTitle>{t('commons:treatment')}</FormGridSectionTitle>
                                        </FormGridSectionItem>
                                        <FormGridSectionItem>
                                            <TreatmentRequestTreatmentForm patientId={patientQuery.data.id} />
                                        </FormGridSectionItem>
                                    </FormGridSection>

                                    <Divider direction="horizontal" />

                                    <FormGridSection>
                                        <FormGridSectionItem>
                                            <FormGridSectionTitle>
                                                {getPatientServiceType(new Date(patientQuery.data.birthDate)).years > 17
                                                    ? t('treatment_requests:score_adults')
                                                    : t('treatment_requests:score_evolutionary_age')}
                                            </FormGridSectionTitle>
                                        </FormGridSectionItem>
                                        <FormGridSectionItem>
                                            <TreatmentRequestScoresForm serviceType={patientQuery.data.serviceType} />
                                        </FormGridSectionItem>
                                    </FormGridSection>

                                    <Divider direction="horizontal" />

                                    <FormGridSection>
                                        <FormGridSectionItem>
                                            <FormGridSectionTitle>{t('commons:notes')}</FormGridSectionTitle>
                                        </FormGridSectionItem>
                                        <FormGridSectionItem>
                                            <TreatmentRequestNotesForm />
                                        </FormGridSectionItem>
                                    </FormGridSection>
                                </>
                            )}

                            {isConfirmationModalVisible && (
                                <CreateTreatmentRequestConfirmationModal
                                    dialogStore={confirmationDialogStore}
                                    onErrorConfirmationModal={onErrorConfirmationModal}
                                    defaultValues={form.getValues()}
                                />
                            )}
                        </FormProvider>
                    </UnsavedBlocker>
                </ScrollArea>
            </PageContent>
        </PageCardWrapper>
    )
}
